import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { TokenCapture } from '../components/idealista/TokenCapture';
import * as idealistaService from '../services/idealista';

export default function IdealistaSetup() {
  const navigate = useNavigate();
  const [isConnected, setIsConnected] = useState(false);
  const [remainingMinutes, setRemainingMinutes] = useState(0);
  const [testResult, setTestResult] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [testData, setTestData] = useState<any>(null);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = () => {
    const hasToken = idealistaService.hasValidToken();
    setIsConnected(hasToken);
    if (hasToken) {
      setRemainingMinutes(idealistaService.getTokenRemainingMinutes());
    }
  };

  const handleTokenSubmit = async (token: string) => {
    // Save token (expires in 1 hour)
    idealistaService.saveToken(token, 1);
    setIsConnected(true);
    setRemainingMinutes(60);

    // Test the connection
    await testConnection();
  };

  const handleDisconnect = () => {
    idealistaService.clearToken();
    setIsConnected(false);
    setTestResult('idle');
    setTestData(null);
  };

  const testConnection = async () => {
    setTestResult('loading');
    try {
      const result = await idealistaService.searchProperties({
        city: 'milano',
        contractType: 'rent',
        limit: 3,
      });
      setTestResult('success');
      setTestData(result);
    } catch (error: any) {
      console.error('Test failed:', error);
      setTestResult('error');
      setTestData({ error: error.message });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-3xl">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Torna indietro
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Configura Idealista</h1>
          <p className="text-gray-600 mt-2">
            Collega il tuo account Idealista per cercare annunci anche su questa piattaforma.
          </p>
        </div>

        {/* Connection Status */}
        {isConnected ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center">
                <CheckCircle className="h-6 w-6 text-green-500 mr-3" />
                <div>
                  <h3 className="font-semibold text-green-800">Idealista collegato!</h3>
                  <p className="text-green-700 text-sm">
                    Token valido per ancora {remainingMinutes} minuti
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={handleDisconnect}>
                Scollega
              </Button>
            </div>

            {/* Test Connection */}
            <div className="mt-4 pt-4 border-t border-green-200">
              <Button
                onClick={testConnection}
                disabled={testResult === 'loading'}
                size="sm"
              >
                {testResult === 'loading' ? 'Testando...' : 'Testa connessione'}
              </Button>

              {testResult === 'success' && testData && (
                <div className="mt-4 p-4 bg-white rounded border">
                  <p className="text-sm text-green-700 font-medium mb-2">
                    Connessione OK! Trovati {testData.data?.length || 0} annunci di test.
                  </p>
                  {testData.data?.slice(0, 2).map((listing: any, idx: number) => (
                    <div key={listing.id || `test-${idx}`} className="text-xs text-gray-600 truncate">
                      - {listing.title} ({listing.priceFormatted})
                    </div>
                  ))}
                </div>
              )}

              {testResult === 'error' && (
                <div className="mt-4 p-4 bg-red-50 rounded border border-red-200">
                  <p className="text-sm text-red-700">
                    Errore: {testData?.error || 'Connessione fallita'}
                  </p>
                  <p className="text-xs text-red-600 mt-1">
                    Il token potrebbe essere scaduto. Prova a ottenerne uno nuovo.
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <TokenCapture onTokenSubmit={handleTokenSubmit} />
        )}

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
          <h3 className="font-semibold text-blue-800 mb-2">Come funziona?</h3>
          <ul className="text-sm text-blue-700 space-y-2">
            <li>1. Il token viene salvato solo nel tuo browser (localStorage)</li>
            <li>2. Non viene mai inviato ai nostri server per essere salvato</li>
            <li>3. Il token scade dopo circa 1 ora, dovrai rinnovarlo</li>
            <li>4. Puoi scollegare Idealista in qualsiasi momento</li>
          </ul>
        </div>

        {/* Alternative: Official API */}
        <div className="bg-gray-100 rounded-lg p-6 mt-6">
          <h3 className="font-semibold text-gray-800 mb-2">Alternativa: API Ufficiale</h3>
          <p className="text-sm text-gray-600 mb-4">
            Se preferisci un accesso stabile e automatizzato, puoi richiedere le credenziali API ufficiali a Idealista.
          </p>
          <a
            href="https://developers.idealista.com/access-request"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            Richiedi accesso API
            <ExternalLink className="h-4 w-4 ml-1" />
          </a>
        </div>
      </div>
    </div>
  );
}
